import { defineStore } from 'pinia';

export type MarketingUtm = {
  id?: string;
  term?: string;
  medium?: string;
  source?: string;
  content?: string;
  campaign?: string;
};

export type GoogleClickConversion = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
};

export type FacebookClickConversion = {
  fbclid?: string;
  fbc?: string;
  fbp?: string;
};

export type TiktokClickConversion = {
  ttclid?: string;
};

export type AptAttribution = {
  channel?: string;
  content?: string;
  extra?: string;
};

// Substitui todos os campos do estado pelos do payload (inclui undefined,
// limpando antigos). Usado quando o grupo vem sempre completo numa
// campanha (UTM, APT).
const replaceAll = <T extends object>(target: T, values: T) => {
  Object.assign(target, values);
};

// Atualiza apenas os campos presentes no payload, preservando os demais.
// Usado para click IDs que podem chegar em momentos diferentes (query
// vs cookie): Google, Facebook, TikTok.
const mergeKeep = <T extends object>(target: T, values: Partial<T>) => {
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) {
      (target as Record<string, unknown>)[key] = value;
    }
  }
};

// 48h — janela de atribuição definida pela equipe de marketing.
export const MARKETING_TRACKING_TTL_MS = 48 * 60 * 60 * 1000;

const isExpired = (timestamp: number | null | undefined) =>
  timestamp != null && Date.now() - timestamp > MARKETING_TRACKING_TTL_MS;

export const useMarketingTrackingStore = defineStore(
  'ap.marketingTracking',
  () => {
    const utm = reactive<MarketingUtm>({});
    const google = reactive<GoogleClickConversion>({});
    const facebook = reactive<FacebookClickConversion>({});
    const tiktok = reactive<TiktokClickConversion>({});
    const apt = reactive<AptAttribution>({});

    const acquiredAt = ref<number | null>(null);

    const markAcquired = () => {
      acquiredAt.value = Date.now();
    };

    // Limpa utm/google/facebook/tiktok. APT não é resetado aqui — quando
    // esta função é chamada, o caller (composable) está prestes a aplicar
    // o novo APT logo em seguida.
    const resetTracking = () => {
      [utm, google, facebook, tiktok].forEach((target) => {
        Object.keys(target).forEach((key) => {
          delete (target as Record<string, unknown>)[key];
        });
      });
    };

    // Limpa tudo (incluindo apt e acquiredAt). Usado pelo afterHydrate
    // quando o timestamp ultrapassa MARKETING_TRACKING_TTL_MS.
    const resetAll = () => {
      resetTracking();
      Object.keys(apt).forEach((key) => {
        delete (apt as Record<string, unknown>)[key];
      });
      acquiredAt.value = null;
    };

    return {
      utm,
      google,
      facebook,
      tiktok,
      apt,
      acquiredAt,
      setUtm: (values: MarketingUtm) => replaceAll(utm, values),
      setApt: (values: AptAttribution) => replaceAll(apt, values),
      setGoogle: (values: Partial<GoogleClickConversion>) =>
        mergeKeep(google, values),
      setFacebook: (values: Partial<FacebookClickConversion>) =>
        mergeKeep(facebook, values),
      setTiktok: (values: Partial<TiktokClickConversion>) =>
        mergeKeep(tiktok, values),
      resetTracking,
      markAcquired,
      resetAll,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
      afterHydrate: (ctx) => {
        const store = ctx.store as ReturnType<typeof useMarketingTrackingStore>;

        if (isExpired(store.acquiredAt)) {
          store.resetAll();
        }
      },
    },
  },
);
